import { AskCustomerButton } from '../../../../customerRequests/CustomerRequestComposer';
import { recipeWriteVersion, StaleRecipeWriteError } from '../../../../../services/recipeWriteConflict';
import { usePracticeDataset } from '../../../../onboarding/practice/practiceDataset';
import { PracticeScopeProvider } from '../../../../onboarding/practice/practiceScope';
import { isPracticeActive, isPracticeId } from '../../../../onboarding/practice/practiceGuards';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../lib/utils';
import { secondaryText, smallTextSize, tertiaryText } from '../../../../../styling/stylingHelper';
import {
  useLoadOrderHistory,
  useLoadRevenueHistory,
  useLoadCostPrice,
  useLoadSubrecipes,
} from '../../../../../utils/dataLoadingUtils';
import { useOrderHistory } from '../../../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../../../stores/appCostPriceCategoriesStore';
import { useRestaurantStore } from '../../../../../stores/appRestaurantStore';
import { useSubrecipesStore } from '../../../../../stores/appSubrecipesStore';
import { useAppUserDefaultStore } from '../../../../../stores/appUserDefaultStore';
import { resolveWastePercentage } from '../../../../../utils/wasteUtils';
import { resolveCostTargets, CostTargets } from '../../../../../utils/costTargets';
import { COST_PRICE_TYPE_CHOICE, calculateSubrecipeUnitCost } from '../../../../../utils/costPriceUtils';
import { computeSubrecipeAncestors } from '../../../../../utils/subrecipeUtils';
import { inventoryKey } from '../../../../../utils/reports/consumptionReport';
import { productKey } from '../../../../../utils/reports/shared';
import {
  CostPriceAnalysis,
  CostPriceIngredient,
  createCostPriceAnalysis,
  updateCostPriceAnalysis,
  fetchCostPriceAnalysisById,
  toIngredientUpdatePayload,
} from '../../../../../services/costPrice/costPriceService';
import CostPriceDetailBody from './CostPriceDetailBody';
import SubrecipeDetailBody from './SubrecipeDetailBody';
import type { SubrecipeSeedIngredient } from './useSubrecipeDetail';
import ProductSetupSelectStep, {
  SetupTab,
  RevenueItemCandidate,
  SubrecipeCandidate,
} from './ProductSetupSelectStep';

/**
 * How to find the SUBJECT behind a setup request. Inventory rows key their order product
 * by `inventoryKey` (raw name / resolver identifier + platform); utilization rows key by
 * the report `productKey` (aggregation identifier). The two schemes are DELIBERATELY
 * separate (see reports/shared.ts) — each surface passes its own kind so neither is ever
 * derived from the other. A counted subrecipe is not a purchased product at all: it names
 * the prep directly and is linked into recipes as a subrecipe reference.
 */
export type ProductSetupMatch =
  | { kind: 'inventory'; inventoryKey: string }
  | { kind: 'report'; reportKey: string }
  | { kind: 'subrecipe'; subrecipeId: string };

/** Raw product identity a host hands to the setup flow. */
export interface ProductSetupIdentity {
  platform: string;
  /** Name used for fuzzy-ranking the candidates + the context header. */
  productName: string;
  /** Optional distinct display name (the counted inventory row's name). */
  displayName?: string;
  match: ProductSetupMatch;
}

interface Props {
  practice?: boolean;
  identity: ProductSetupIdentity;
  restaurantId: string;
  onClose: () => void;
}

/**
 * One thing the product gets linked into. A revenue item resolves to a cost price
 * analysis (created or appended to); a subrecipe is either an existing one or, with
 * `id: null`, a new one to create with this product as its first ingredient.
 */
type SetupTarget =
  | { kind: 'revenueItem'; name: string }
  | { kind: 'subrecipe'; id: string | null };

/** Stable per-target key for the auto-link guards + resolved-id/error maps. */
const targetKey = (target: SetupTarget): string =>
  target.kind === 'revenueItem' ? `revenue:${target.name}` : `sub:${target.id ?? 'new'}`;

/** Tokens for fuzzy matching: lowercase words of 3+ chars, no pure numbers (vintages). */
function tokenize(s: string): Set<string> {
  return new Set(
    s
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((tok) => tok.length >= 3 && !/^\d+$/.test(tok))
  );
}

const MAX_LIST_ROWS = 50;

/**
 * Slide-over that walks an unlinked order product into cost-price items and/or
 * subrecipes. The stepper spans every step: step 1 chooses the targets (two tabs —
 * revenue items and subrecipes — whose selections accumulate into ONE list), then one
 * step per chosen target.
 *
 * Revenue-item steps AUTO-link on entry: they create a new cost price (single
 * ingredient, default qty) when none exists, or append the product as an ingredient to
 * the existing analysis (skipping silently if it's already on it by resolver key, as
 * ingredient or alternative), then embed the REAL cost-price editor with the just-added
 * row focused. Every write upserts into the cost-price store, so the inventory
 * "not setup" list and the utilization "purchased but not linked" table recompute
 * without a refetch. Closing mid-flow keeps whatever was written; back/forward never
 * double-links (guarded).
 *
 * Subrecipe steps embed the REAL subrecipe editor with the product seeded as a draft
 * row. Subrecipes persist their whole ingredient array in one call, so that step keeps
 * the editor's own Create/Update button — saving advances the stepper.
 */
export default function ProductSetupModal({ identity, restaurantId, onClose, practice = false }: Props) {
  const { t } = useTranslation();

  // The embedded editors self-load cost price / subrecipes; we still load order +
  // revenue here for the selection step's ranking + existing-analysis lookup, and
  // subrecipes for the subrecipe tab's list.
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadCostPrice();
  useLoadSubrecipes();

  const fixture = usePracticeDataset(practice);
  const liveOrders = useOrderHistory((s) => s.orderHistoryList);
  const ordersLoading = useOrderHistory((s) => s.isLoading);
  const liveRevenue = useRevenueHistory((s) => s.revenueHistoryList);
  const revenueLoading = useRevenueHistory((s) => s.isLoading);
  const liveCostPriceList = useCostPriceStore((s) => s.costPriceList);
  const liveCustomIngredients = useCostPriceStore((s) => s.customIngredients);
  const upsertOne = useCostPriceStore((s) => s.upsertOne);
  const liveCategories = useCostPriceCategoriesStore((s) => s.categories);
  const liveSubrecipesList = useSubrecipesStore((s) => s.subrecipesList);
  const liveSubrecipesById = useSubrecipesStore((s) => s.subrecipesById);
  const subrecipesLoading = useSubrecipesStore((s) => s.isLoading);
  // Practice remains an instance property after teardown: a stale editor never adopts live rows.
  const orders = useMemo(() => practice ? fixture?.orderHistoryList ?? [] : liveOrders, [practice, fixture?.orderHistoryList, liveOrders]);
  const revenue = useMemo(() => practice ? fixture?.revenueHistoryList ?? [] : liveRevenue, [practice, fixture?.revenueHistoryList, liveRevenue]);
  const costPriceList = useMemo(() => practice ? fixture?.costPriceList ?? [] : liveCostPriceList, [practice, fixture?.costPriceList, liveCostPriceList]);
  const customIngredients = useMemo(() => practice ? fixture?.customIngredients ?? [] : liveCustomIngredients, [practice, fixture?.customIngredients, liveCustomIngredients]);
  const categories = useMemo(() => practice ? fixture?.categories ?? [] : liveCategories, [practice, fixture?.categories, liveCategories]);
  const subrecipesList = useMemo(() => practice ? fixture?.subrecipesList ?? [] : liveSubrecipesList, [practice, fixture?.subrecipesList, liveSubrecipesList]);
  const subrecipesById = useMemo(() => practice ? fixture?.subrecipesById ?? new Map() : liveSubrecipesById, [practice, fixture?.subrecipesById, liveSubrecipesById]);
  const showExVat = useAppUserDefaultStore((s) => s.showExVatOrders);
  const reportSettings = useRestaurantStore((s) => s.reportSettings[restaurantId]);
  const defaultWastePercentage = practice ? 0 : resolveWastePercentage(reportSettings?.wastePercentage);
  const costTargets: CostTargets = useMemo(() => resolveCostTargets(reportSettings?.costTargets), [reportSettings]);

  // --- Slide-in animation ---
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  useEffect(() => {
    setIsPanelVisible(true);
  }, []);
  const handleClose = () => {
    setIsPanelVisible(false);
    setTimeout(onClose, 200);
  };

  const [activeTab, setActiveTab] = useState<SetupTab>('revenue');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(''); // '' = all
  const [subrecipeSearch, setSubrecipeSearch] = useState('');
  const [targets, setTargets] = useState<SetupTarget[]>([]);
  // Step 0 = choose targets; steps 1..N = one per selected target.
  const [currentStep, setCurrentStep] = useState(0);
  /** Target key → id of the analysis it was linked into (created or existing). */
  const [resolvedId, setResolvedId] = useState<Record<string, string>>({});
  /** Per-target so pending/failed auto-links never bleed across steps. */
  const [processError, setProcessError] = useState<Record<string, boolean>>({});
  // Target keys whose auto-link has been STARTED — synchronous guard so the advance
  // effect (incl. React 18 StrictMode's double-invoke) never links the same one twice.
  const startedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!practice) return;
    const tab = (e: Event) => setActiveTab((e as CustomEvent).detail?.tab === 'subrecipe' ? 'subrecipe' : 'revenue');
    const searchEvent = (e: Event) => { const q = (e as CustomEvent).detail?.query ?? ''; setSearch(q); setSubrecipeSearch(q); };
    const select = (e: Event) => {
      const d = (e as CustomEvent).detail ?? {};
      if ('subrecipeId' in d && (d.subrecipeId === null || fixture?.subrecipesById.has(d.subrecipeId))) setTargets([{ kind: 'subrecipe', id: d.subrecipeId }]);
      else if (fixture?.revenueItems.includes(d.name)) setTargets([{ kind: 'revenueItem', name: d.name }]);
    };
    const reset = () => setCurrentStep(0);
    const next = () => { if (targets.length && isPracticeActive()) setCurrentStep(s => s === 0 ? 1 : s); };
    const events = { 'onboarding:inventory-setup-reset': reset, 'onboarding:inventory-setup-tab': tab, 'onboarding:inventory-setup-search': searchEvent,
      'onboarding:inventory-setup-select': select, 'onboarding:inventory-setup-continue': next };
    Object.entries(events).forEach(([name, listener]) => window.addEventListener(name, listener));
    return () => Object.entries(events).forEach(([name, listener]) => window.removeEventListener(name, listener));
  }, [practice, fixture, targets]);

  const isLoading = (ordersLoading && orders.length === 0) || (revenueLoading && revenue.length === 0);

  // What is being linked. An ORDER PRODUCT subject supplies canonical articleId / name /
  // platform / quantityInfo from its latest order line (matched per source's own key scheme,
  // never one derived from the other); a SUBRECIPE subject is the prep itself, resolved
  // straight from the subrecipes store. Everything downstream — the seed row, the auto-link
  // row, the dedup guard, the candidate list — branches on this one value.
  const subject = useMemo(() => {
    if (identity.match.kind === 'subrecipe') {
      const sub = subrecipesById.get(identity.match.subrecipeId);
      return sub ? ({ kind: 'subrecipe', subrecipe: sub } as const) : null;
    }
    const match = identity.match;
    const sorted = [...orders].sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
    for (const o of sorted) {
      for (const p of o.productData) {
        const matches =
          match.kind === 'inventory'
            ? inventoryKey(o.platform, p.articleId, p.name) === match.inventoryKey
            : productKey(o.platform, p.articleId, p.name) === match.reportKey;
        if (matches) return { kind: 'orderProduct', platform: o.platform, prod: p } as const;
      }
    }
    return null;
  }, [orders, identity, subrecipesById]);

  const orderSubject = subject?.kind === 'orderProduct' ? subject : null;
  const subrecipeSubject = subject?.kind === 'subrecipe' ? subject.subrecipe : null;

  // Resolver key of the PRODUCT being linked — the same key both editors use to dedup /
  // focus an ingredient row. A prep subject has none (it is not a purchased product); the
  // editors then simply open unfocused.
  const focusKey = orderSubject ? productKey(orderSubject.platform, orderSubject.prod.articleId, orderSubject.prod.name) : '';

  /** Default amount for a freshly linked row: 100 for weight/volume, else 1. */
  const defaultQuantity = (stdUnit?: string) => (stdUnit === 'g' || stdUnit === 'ml' ? 100 : 1);

  // The draft row a subrecipe step starts with. Memoized: the hook reads it once,
  // but a stable identity keeps the editor from re-seeding on unrelated renders.
  // A prep subject seeds a NESTED-subrecipe reference row instead of an ingredient row.
  const seedIngredient = useMemo<SubrecipeSeedIngredient | undefined>(() => {
    if (subrecipeSubject) {
      return {
        nestedSubrecipeId: subrecipeSubject.id,
        nestedSubrecipePortionsNeeded: defaultQuantity(subrecipeSubject.portionsUnit),
      };
    }
    if (!orderSubject) return undefined;
    const qi = orderSubject.prod.quantityInfo;
    return {
      articleId: orderSubject.prod.articleId,
      productName: orderSubject.prod.name,
      quantityNeeded: defaultQuantity(qi?.stdUnit),
      platform: orderSubject.platform,
      customPlatformName: (orderSubject.prod as { customPlatformName?: string }).customPlatformName,
      quantityInfo: qi || { stdAmount: 1, stdMultiplier: 1, stdUnit: 'piece' },
      isCustom: false,
    };
  }, [orderSubject, subrecipeSubject]);

  // Existing (non-CHOICE) analysis per linked revenue item name.
  const existingByName = useMemo(() => {
    const map = new Map<string, CostPriceAnalysis>();
    for (const c of costPriceList) {
      if (c.costPriceType === COST_PRICE_TYPE_CHOICE || !c.menuItemName) continue;
      if (!map.has(c.menuItemName)) map.set(c.menuItemName, c);
    }
    return map;
  }, [costPriceList]);

  const categoryNameFor = useCallback(
    (name: string): string | null => {
      const ex = existingByName.get(name);
      if (!ex?.categoryId) return null;
      return categories.find((k) => k.id === ex.categoryId)?.name ?? null;
    },
    [existingByName, categories]
  );

  // ALL revenue items ever sold, ranked by fuzzy name match, then recent volume, then recency.
  const candidates = useMemo(() => {
    const productTokens = tokenize(identity.productName);
    const cutoff30 = Date.now() - 30 * 86_400_000;
    const byName = new Map<string, { qty30: number; lastSold: Date }>();
    for (const r of revenue) {
      const date = new Date(r.date);
      const recent = date.getTime() >= cutoff30;
      for (const cat of r.revenueData.categories) {
        for (const item of cat.items) {
          if (!item.name) continue;
          const entry = byName.get(item.name);
          if (entry) {
            if (recent) entry.qty30 += item.quantity || 0;
            if (date > entry.lastSold) entry.lastSold = date;
          } else {
            byName.set(item.name, { qty30: recent ? item.quantity || 0 : 0, lastSold: date });
          }
        }
      }
    }
    const list: RevenueItemCandidate[] = Array.from(byName.entries()).map(([name, info]) => {
      const itemTokens = tokenize(name);
      let score = 0;
      productTokens.forEach((tok) => {
        if (itemTokens.has(tok)) score += 1;
      });
      return { name, qty30: info.qty30, lastSold: info.lastSold, score };
    });
    list.sort((a, b) => b.score - a.score || b.qty30 - a.qty30 || b.lastSold.getTime() - a.lastSold.getTime());
    return list;
  }, [revenue, identity.productName]);

  // Category names present among candidates (via their existing analyses) — the
  // filter dropdown's options.
  const candidateCategoryNames = useMemo(() => {
    const set = new Set<string>();
    for (const c of candidates) {
      const name = categoryNameFor(c.name);
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [candidates, categoryNameFor]);

  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter(
      (c) =>
        (q === '' || c.name.toLowerCase().includes(q)) &&
        (categoryFilter === '' || categoryNameFor(c.name) === categoryFilter)
    );
  }, [candidates, search, categoryFilter, categoryNameFor]);

  // Dishes (non-CHOICE analyses with a revenue link) per DIRECTLY referenced subrecipe.
  const dishIdsBySubrecipeId = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const c of costPriceList) {
      if (c.costPriceType === COST_PRICE_TYPE_CHOICE || !c.menuItemName) continue;
      for (const ing of c.ingredients) {
        if (!ing.subrecipeId) continue;
        let set = map.get(ing.subrecipeId);
        if (!set) {
          set = new Set<string>();
          map.set(ing.subrecipeId, set);
        }
        set.add(c.id);
      }
    }
    return map;
  }, [costPriceList]);

  // Subrecipes ranked like the revenue list (fuzzy name match first), each carrying
  // its live cost per unit and how many dishes reach it. "Reach" is transitive: a
  // subrecipe nested inside one a dish uses IS used — `computeSubrecipeAncestors`
  // returns exactly the subrecipes whose tree contains it.
  const subrecipeCandidates = useMemo<SubrecipeCandidate[]>(() => {
    const productTokens = tokenize(identity.productName);
    const all = Array.from(subrecipesById.values());
    const subjectId = subrecipeSubject?.id;
    const list: SubrecipeCandidate[] = [];
    for (const sub of subrecipesList) {
      const reach = computeSubrecipeAncestors(all, sub.id);
      // Nesting the subject INTO this candidate adds the edge candidate → subject, so a
      // candidate the subject can already reach (i.e. one the subject is an ancestor of)
      // would close a cycle. Itself likewise. The server re-validates either way.
      if (subjectId !== undefined && (sub.id === subjectId || reach.has(subjectId))) continue;
      reach.add(sub.id);
      const dishes = new Set<string>();
      reach.forEach((id) => dishIdsBySubrecipeId.get(id)?.forEach((cpId) => dishes.add(cpId)));
      const nameTokens = tokenize(sub.name);
      let score = 0;
      productTokens.forEach((tok) => {
        if (nameTokens.has(tok)) score += 1;
      });
      list.push({
        id: sub.id,
        name: sub.name,
        portions: sub.portions,
        portionsUnit: sub.portionsUnit,
        ingredientCount: sub.ingredients.length,
        unitCost: calculateSubrecipeUnitCost(
          sub,
          orders,
          customIngredients,
          showExVat,
          subrecipesById,
          defaultWastePercentage
        ),
        usedInDishes: dishes.size,
        score,
      });
    }
    list.sort((a, b) => b.score - a.score || b.usedInDishes - a.usedInDishes || a.name.localeCompare(b.name));
    return list;
  }, [
    subrecipesList,
    subrecipesById,
    subrecipeSubject,
    dishIdsBySubrecipeId,
    identity.productName,
    orders,
    customIngredients,
    showExVat,
    defaultWastePercentage,
  ]);

  const filteredSubrecipes = useMemo(() => {
    const q = subrecipeSearch.trim().toLowerCase();
    return q === '' ? subrecipeCandidates : subrecipeCandidates.filter((s) => s.name.toLowerCase().includes(q));
  }, [subrecipeCandidates, subrecipeSearch]);

  // --- Selection ---
  const toggleTarget = (target: SetupTarget) => {
    const key = targetKey(target);
    setTargets((prev) => (prev.some((x) => targetKey(x) === key) ? prev.filter((x) => targetKey(x) !== key) : [...prev, target]));
  };
  const isSelected = (target: SetupTarget) => targets.some((x) => targetKey(x) === targetKey(target));

  const revenueSelectedCount = targets.filter((x) => x.kind === 'revenueItem').length;
  const subrecipeSelectedCount = targets.length - revenueSelectedCount;

  // Auto-link the product into this revenue item's analysis: create-with-ingredient
  // when none exists, else append the ingredient (skip if already present by resolver
  // key, as ingredient OR alternative). Mirrors useCostPriceDetail's fresh-fetch save.
  const processRevenueItem = useCallback(
    async (name: string) => {
      if (!subject || (practice && !isPracticeActive())) return;
      const qi = orderSubject?.prod.quantityInfo;
      // A prep goes in as a subrecipe REFERENCE row (the cost calc resolves its ingredients
      // live); an order product goes in as a normal ingredient row.
      const newRow: Omit<CostPriceIngredient, 'id' | 'currentPrice' | 'currentPriceDate'> = subrecipeSubject
        ? {
            subrecipeId: subrecipeSubject.id,
            subrecipePortionsNeeded: defaultQuantity(subrecipeSubject.portionsUnit),
          }
        : {
            articleId: orderSubject!.prod.articleId,
            productName: orderSubject!.prod.name,
            quantityNeeded: defaultQuantity(qi?.stdUnit),
            quantityInfo: qi || { stdAmount: 1, stdMultiplier: 1, stdUnit: 'piece' },
            platform: orderSubject!.platform,
            customPlatformName: (orderSubject!.prod as { customPlatformName?: string }).customPlatformName,
          };
      const target = orderSubject
        ? productKey(orderSubject.platform, orderSubject.prod.articleId, orderSubject.prod.name)
        : '';
      const key = targetKey({ kind: 'revenueItem', name });
      setProcessError((prev) => ({ ...prev, [key]: false }));
      try {
        const existing = existingByName.get(name);
        if (practice && existing && !isPracticeId(existing.id)) return;
        let id: string;
        if (!existing) {
          const saved = await createCostPriceAnalysis({
            restaurantId,
            costPriceName: name,
            menuItemName: name,
            ingredients: [newRow],
          });
          if (!practice) upsertOne(saved);
          id = saved.id;
        } else {
          const current = await fetchCostPriceAnalysisById(existing.id);
          const already = current.ingredients.some((ing) => {
            // A prep is already on the recipe when it is referenced by id — there is no
            // product identity (nor alternatives) to compare.
            if (subrecipeSubject) return ing.subrecipeId === subrecipeSubject.id;
            const isNormal = !ing.custom && !(ing.subrecipeId && !ing.articleId) && !ing.nestedCostPriceId;
            if (isNormal && productKey(ing.platform ?? '', ing.articleId, ing.productName) === target) return true;
            return (ing.alternatives ?? []).some((a) => productKey(a.platform, a.articleId, a.productName) === target);
          });
          if (already) {
            id = existing.id;
          } else {
            const updated = await updateCostPriceAnalysis({
              id: existing.id,
              expectedUpdatedAt: recipeWriteVersion(current),
              ingredients: [...current.ingredients.map(toIngredientUpdatePayload), newRow],
            });
            if (!practice) upsertOne(updated);
            id = updated.id;
          }
        }
        // upsertOne has run before the body mounts, so it renders the added row.
        setResolvedId((prev) => ({ ...prev, [key]: id }));
      } catch (err) {
        if (err instanceof StaleRecipeWriteError) alert(err.message);
        console.error('Failed to auto-link setup product:', err);
        setProcessError((prev) => ({ ...prev, [key]: true }));
      }
    },
    [subject, orderSubject, subrecipeSubject, existingByName, restaurantId, upsertOne, practice]
  );

  // --- Step derived ---
  const isSelectionStep = currentStep === 0;
  const currentTarget: SetupTarget | undefined = currentStep >= 1 ? targets[currentStep - 1] : undefined;
  const currentKey = currentTarget ? targetKey(currentTarget) : '';
  const currentResolvedId = currentKey ? resolvedId[currentKey] : undefined;
  const isLastStep = currentStep === targets.length;
  const totalSteps = targets.length;

  // On entering a REVENUE-item step for the first time, auto-link its product. The ref
  // guard makes this idempotent across back/forward navigation and StrictMode
  // double-invokes. Subrecipe steps never auto-write — their editor owns the save.
  useEffect(() => {
    if (currentStep === 0 || !subject) return;
    const target = targets[currentStep - 1];
    if (!target || target.kind !== 'revenueItem') return;
    const key = targetKey(target);
    if (startedRef.current.has(key)) return;
    startedRef.current.add(key);
    processRevenueItem(target.name);
  }, [currentStep, subject, targets, processRevenueItem]);

  const retry = (name: string) => {
    startedRef.current.add(targetKey({ kind: 'revenueItem', name }));
    processRevenueItem(name);
  };

  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));
  const goNext = () => {
    if (isLastStep) {
      handleClose();
      return;
    }
    setCurrentStep((s) => s + 1);
  };

  // A create-new subrecipe step becomes a normal edit target once saved, so stepping
  // back into it edits that subrecipe instead of creating a second one.
  const handleSubrecipeSaved = (saved?: { id: string }) => {
    if (saved && currentTarget?.kind === 'subrecipe' && currentTarget.id === null) {
      const stepIndex = currentStep - 1;
      setTargets((prev) => prev.map((x, i) => (i === stepIndex ? { kind: 'subrecipe', id: saved.id } : x)));
    }
    goNext();
  };

  const contextName = identity.displayName ?? identity.productName;

  // "Nothing to set up" means the SUBJECT itself can't be resolved: no order line behind
  // the product, or a subrecipe that was deleted. Each kind waits on its own source, and
  // an empty source (nothing loaded at all) is not treated as a miss.
  const subjectSourceHasData =
    identity.match.kind === 'subrecipe' ? !subrecipesLoading && subrecipesList.length > 0 : orders.length > 0;
  const showNotFound = !isLoading && subjectSourceHasData && !subject;

  // ---------- Progress stepper bars (shared by the header + the embeds' subHeader) ----------
  // Each bar jumps straight to its step. Entering an unlinked revenue step auto-links
  // it via the advance effect exactly as forward-navigation does. Bar i maps to step
  // i+1 (step 0 is the selection step, reachable via the Back button).
  const stepperBars = (
    <div className="flex gap-1.5">
      {Array.from({ length: totalSteps }).map((_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => setCurrentStep(i + 1)}
          aria-label={t('productSetup.step', { current: i + 1, total: totalSteps }) as string}
          className={cn(
            'h-1.5 flex-1 rounded-full transition-colors cursor-pointer',
            i < currentStep ? 'bg-[#7A5AF8]' : 'bg-[#D1D1D6] dark:bg-[#E8E8E9]'
          )}
        />
      ))}
    </div>
  );

  // A subrecipe that no dish reaches keeps the product in the utilization
  // "purchased, not linked" list (that view is sales-weighted) — say so up front
  // rather than letting the row look like the setup did nothing.
  const orphanSubrecipeNote =
    currentTarget?.kind === 'subrecipe' &&
    currentTarget.id !== null &&
    subrecipeCandidates.find((s) => s.id === currentTarget.id)?.usedInDishes === 0;

  // Strip rendered below each embedded editor's own header (via its subHeader prop) so
  // the step progress stays visible while the real editor is mounted.
  const stepperStrip = (
    <div className="px-4 sm:px-5 py-3 border-b border-[#E8E8E9] dark:border-[#1E3A4E]">
      <span className="block mb-2.5 text-[16px] font-bold text-[#0C111D] dark:text-[#CBD5E1]">
        {t('productSetup.step', { current: currentStep, total: totalSteps })}
      </span>
      {stepperBars}
      {orphanSubrecipeNote && (
        <p className="mt-2.5 text-[13px] leading-5 text-amber-700 dark:text-amber-200">
          {t('productSetup.orphanSubrecipeNote')}
        </p>
      )}
    </div>
  );

  const backBar = (
    <div className="flex items-center px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E8E8E9] dark:border-[#1E3A4E] w-full">
      <button
        type="button"
        onClick={handleClose}
        className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-[#13354F] hover:bg-gray-50 dark:bg-[#13354F] transition-colors"
        aria-label={t('common.cancel') as string}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="dark:text-[#CBD5E1] text-[#000]" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.8337 10H4.16699" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10.0003 15.8332L4.16699 9.99984L10.0003 4.1665" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );

  // Selection step: the title is the topmost content (the selected-count line moved
  // into the tab badges + the footer button, and the product name now shows once, in
  // the context card).
  const selectionHeader = (
    <div className="flex items-start flex-col justify-between">
      {backBar}
      <div className="min-w-0 w-full px-4 sm:px-5 py-3 sm:py-5">
        <h2 className="mb-3 text-[28px] leading-[39px] font-bold text-[#161924] dark:text-zinc-50">
          {t('productSetup.title')}
        </h2>
        <p className={cn(smallTextSize, secondaryText, 'text-[16px] leading-[21px] font-normal')}>
          {subrecipeSubject
            ? activeTab === 'revenue'
              ? t('productSetup.prepDescription')
              : t('productSetup.prepDescriptionSubrecipe')
            : activeTab === 'revenue'
              ? t('productSetup.description')
              : t('productSetup.descriptionSubrecipe')}
        </p>
      </div>
    </div>
  );

  // Transient header for a revenue step while its auto-link runs (or failed) — the
  // editor brings its own header once it mounts.
  const processingHeader = (
    <div className="flex items-start flex-col justify-between">
      {backBar}
      <div className="min-w-0 w-full px-4 sm:px-5 py-3 sm:py-5">
        <div className="mb-[16px]">
          <span className="block mb-2.5 text-[16px] font-bold text-[#0C111D] dark:text-[#CBD5E1]">
            {t('productSetup.step', { current: currentStep, total: totalSteps })}
          </span>
          {stepperBars}
        </div>
        <h2 className="mb-3 text-[28px] leading-[39px] font-bold text-[#161924] dark:text-zinc-50">
          {t('productSetup.title')}
        </h2>
        {currentTarget?.kind === 'revenueItem' && (
          <p className={cn(smallTextSize, secondaryText, 'text-[16px] leading-[21px] font-normal')}>
            {currentTarget.name}
          </p>
        )}
      </div>
    </div>
  );

  const selectionFooter = (
    <div className="flex gap-3 sm:gap-6 px-4 sm:px-5 pb-4 sm:pb-5 flex-shrink-0">
      <button
        onClick={handleClose}
        className="bg-white flex-1 px-6 py-[11px] rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#F8FAFC] disabled:opacity-50 transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]"
      >
        {t('common.cancel')}
      </button>
      {!practice && orderSubject && <AskCustomerButton seed={{ restaurantId, type: 'ingredient_dish_usage', mode: 'add_missing', productName: orderSubject.prod.name }} />}
      <button
        data-onboarding={practice ? "inventory-setup-continue" : undefined}
        onClick={() => setCurrentStep(1)}
        disabled={targets.length === 0 || !subject}
        className="flex items-center gap-1.5 flex-1 justify-center px-6 py-[11px] rounded-xl text-[14px] leading-5 font-semibold text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {t('productSetup.continueWithItems', { count: targets.length })}
      </button>
    </div>
  );

  // Revenue steps only: the subrecipe editor brings its own Cancel/Save footer.
  const itemFooter = (
    <div className="flex gap-3 sm:gap-6 px-4 sm:px-5 pb-4 sm:pb-5 flex-shrink-0">
      <button
        onClick={goBack}
        className="bg-white flex-1 px-6 py-[11px] rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#F8FAFC] disabled:opacity-50 transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]"
      >
        {t('productSetup.back')}
      </button>
      <button
        data-onboarding={practice ? "inventory-setup-done" : undefined}
        onClick={goNext}
        disabled={!currentResolvedId}
        className="flex items-center gap-1.5 flex-1 justify-center px-6 py-[11px] rounded-xl text-[14px] leading-5 font-semibold text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {isLastStep ? t('productSetup.done') : t('productSetup.saveAndContinue')}
      </button>
    </div>
  );

  return createPortal(
    <PracticeScopeProvider value={practice}>
    <div className="fixed inset-0 z-50 flex justify-end pt-[72px] md:pt-0">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 dark:bg-black/60 transition-opacity duration-300" onClick={handleClose} />
      <div
        data-onboarding={practice ? "inventory-setup" : undefined}
        className={cn(
          'h-full w-full bg-white dark:bg-[#0A2133] shadow-xl flex flex-col transform transition-all duration-250 ease-out max-w-full md:max-w-[584px]',
          isPanelVisible ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {isSelectionStep ? (
          <>
            {selectionHeader}
            <ProductSetupSelectStep
              contextName={contextName}
              linkedProductName={orderSubject && orderSubject.prod.name !== contextName ? orderSubject.prod.name : undefined}
              showNotFound={showNotFound}
              isSubrecipeSubject={identity.match.kind === 'subrecipe'}
              isLoading={isLoading}
              isLoadingSubrecipes={subrecipesLoading && subrecipesList.length === 0}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              revenueSelectedCount={revenueSelectedCount}
              subrecipeSelectedCount={subrecipeSelectedCount}
              search={search}
              onSearchChange={setSearch}
              categoryFilter={categoryFilter}
              onCategoryFilterChange={setCategoryFilter}
              categoryNames={candidateCategoryNames}
              revenueCandidates={filteredCandidates.slice(0, MAX_LIST_ROWS)}
              revenueMatchCount={filteredCandidates.length}
              hiddenRevenueCount={Math.max(0, filteredCandidates.length - MAX_LIST_ROWS)}
              isRevenueSelected={(name) => isSelected({ kind: 'revenueItem', name })}
              onToggleRevenue={(name) => toggleTarget({ kind: 'revenueItem', name })}
              hasAnalysis={(name) => existingByName.has(name)}
              categoryNameFor={categoryNameFor}
              subrecipeSearch={subrecipeSearch}
              onSubrecipeSearchChange={setSubrecipeSearch}
              subrecipeCandidates={filteredSubrecipes.slice(0, MAX_LIST_ROWS)}
              subrecipeMatchCount={filteredSubrecipes.length}
              hiddenSubrecipeCount={Math.max(0, filteredSubrecipes.length - MAX_LIST_ROWS)}
              isSubrecipeSelected={(id) => isSelected({ kind: 'subrecipe', id })}
              onToggleSubrecipe={(id) => toggleTarget({ kind: 'subrecipe', id })}
              isCreateNewSelected={isSelected({ kind: 'subrecipe', id: null })}
              onToggleCreateNew={() => toggleTarget({ kind: 'subrecipe', id: null })}
            />
            {selectionFooter}
          </>
        ) : currentTarget?.kind === 'subrecipe' ? (
          <div className="flex-1 min-h-0 flex flex-col">
            <SubrecipeDetailBody
              key={currentKey}
              subrecipeId={currentTarget.id}
              onClose={handleClose}
              onSaved={handleSubrecipeSaved}
              closeOnSave={false}
              host="embedded"
              subHeader={stepperStrip}
              initialFocusProductKey={focusKey}
              seedIngredient={seedIngredient}
              onBack={goBack}
            />
          </div>
        ) : currentTarget?.kind === 'revenueItem' && currentResolvedId ? (
          <>
            <div className="flex-1 min-h-0 flex flex-col">
              <CostPriceDetailBody
                key={currentResolvedId}
                costPriceId={currentResolvedId}
                onClose={handleClose}
                host="embedded"
                costTargets={costTargets}
                defaultWastePercentage={defaultWastePercentage}
                subHeader={stepperStrip}
                initialFocusProductKey={focusKey}
              />
            </div>
            {itemFooter}
          </>
        ) : (
          <>
            {processingHeader}
            <div className="flex-1 flex items-center justify-center p-6 text-center">
              {currentTarget?.kind === 'revenueItem' && processError[currentKey] ? (
                <div className="flex flex-col items-center gap-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{t('productSetup.addFailed')}</p>
                  <button
                    type="button"
                    onClick={() => retry(currentTarget.name)}
                    className="px-5 py-2 rounded-lg border border-[#E8E8E9] dark:border-[#2D4F68] text-[#475467] dark:text-[#CBD5E1] hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {t('productSetup.retry')}
                  </button>
                </div>
              ) : (
                <p className={cn(smallTextSize, tertiaryText)}>{t('productSetup.adding')}</p>
              )}
            </div>
            {itemFooter}
          </>
        )}
      </div>
    </div>
    </PracticeScopeProvider>,
    document.body
  );
}
